import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PromoBanner from "../components/PromoBanner";
import { Check, X as XIcon, Sparkles, Star, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const HEADLINE = "Trade Smarter. Not Harder.";
const SUBHEADLINE =
  "Professional market scanning, real-time alerts, AI-powered analysis, and advanced backtesting in one platform.";
const HIGHLIGHTS = [
  "No commissions charged by FlowFinder",
  "No profit sharing",
  "Flat monthly pricing",
  "Works with your preferred broker",
  "Professional-grade scanner",
  "Institutional-grade backtesting",
];

const COMPARE = [
  { label: "Watchlists",             free: "1 × 5 syms",   premium: "Unlimited × 30",  executive: "Unlimited × 1,000" },
  { label: "Scanner",                free: "Limited",      premium: "Full live",       executive: "Full live (priority)" },
  { label: "Real-time market data",  free: false,          premium: true,              executive: true },
  { label: "SMS Alerts (Twilio)",    free: false,          premium: true,              executive: true },
  { label: "In-app Alerts",          free: "3 active",     premium: "Unlimited",       executive: "Unlimited" },
  { label: "AI Trading Assistant",   free: false,          premium: true,              executive: true },
  { label: "Custom Filters",         free: false,          premium: true,              executive: true },
  { label: "Full indicator library", free: "RSI/MACD/VWAP",premium: true,              executive: true },
  { label: "Professional Backtester",free: false,          premium: false,             executive: true },
  { label: "Saved Strategies",       free: false,          premium: false,             executive: true },
  { label: "Strategy Comparison",    free: false,          premium: false,             executive: true },
  { label: "Trade Replay",           free: false,          premium: false,             executive: true },
  { label: "CSV / Excel / PDF export",free: false,         premium: false,             executive: true },
  { label: "Executive badge",        free: false,          premium: false,             executive: true },
  { label: "Support",                free: "Community",    premium: "Email",           executive: "Priority" },
];

const FAQ = [
  { q: "Does FlowFinder execute trades?",
    a: "No. FlowFinder is a market scanner, analytics and backtesting platform. Trades are executed through your own brokerage account." },
  { q: "Does FlowFinder charge commissions?",
    a: "No. FlowFinder only charges a monthly subscription. Any brokerage fees or trading costs are determined by your broker." },
  { q: "How does the Launch Sale work?",
    a: "Premium and Executive are 50% off during our launch. If you subscribe now, you keep this launch price forever — as long as your subscription stays active. Cancelling means the discount ends." },
  { q: "Can I cancel anytime?",
    a: "Yes. Cancel from Account Settings. Your data is preserved and you're moved to Free at the end of the current billing period." },
  { q: "How does the 7-day trial work?",
    a: "Every new account gets 7 days of full Executive access — no card required. When it ends, you're moved to Free unless you upgrade." },
];

function Cell({ value, accent }) {
  if (value === true) return <Check className="w-4 h-4 inline" style={{ color: accent }} />;
  if (value === false) return <XIcon className="w-4 h-4 inline text-white/25" />;
  return <span className="text-white/85 text-[13px]">{value}</span>;
}

function PlanCol({ plan, currentPlan, isTrial, onUpgrade, busyPlan, promotion }) {
  const isFree = plan.id === "free";
  const cta = isFree
    ? currentPlan === "free" ? "Current plan" : "Downgrade"
    : currentPlan === plan.id && !isTrial ? "Current plan"
    : plan.id === "executive" && isTrial ? "Trial active"
    : `Upgrade to ${plan.name}`;
  const disabled = (isFree && currentPlan === "free") || (currentPlan === plan.id && !isTrial);
  const accent = plan.id === "executive" ? "#ffd166" : plan.id === "premium" ? "var(--bull)" : "#9aa0aa";
  const isHighlighted = plan.highlight === "most popular";
  const isFlagship = plan.highlight === "flagship";
  const promoBadge = plan.id === "premium" ? promotion?.badge_text_premium
                    : plan.id === "executive" ? promotion?.badge_text_executive
                    : null;
  const displayBadge = promoBadge || plan.badge;
  const saved = plan.save_pct;

  return (
    <div
      className={`card-surface p-5 sm:p-6 relative overflow-hidden flex flex-col ${
        isHighlighted ? "border-[var(--bull)]/45" : isFlagship ? "border-[#ffd166]/45" : ""
      }`}
      style={
        isHighlighted ? { boxShadow: "0 0 40px rgba(0,255,156,0.14)" }
        : isFlagship ? { boxShadow: "0 0 40px rgba(255,209,102,0.14)" }
        : undefined
      }
      data-testid={`plan-card-${plan.id}`}
    >
      <div className="absolute inset-0 pointer-events-none opacity-50"
           style={{ background: `radial-gradient(circle at 100% 0%, ${accent}22, transparent 60%)` }} />
      <div className="relative flex-1">
        {displayBadge && (
          <div
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-widest font-mono uppercase mb-3 ${
              isFlagship
                ? "bg-[#ffd166]/15 border border-[#ffd166]/40 text-[#ffd166]"
                : "bg-[var(--bull)]/15 border border-[var(--bull)]/40 text-[var(--bull)]"
            }`}
            data-testid={`plan-badge-${plan.id}`}
          >
            {isFlagship ? <Crown className="w-3 h-3" /> : <Star className="w-3 h-3" />}
            {displayBadge}
          </div>
        )}
        <div className="text-white text-[15px] font-semibold tracking-tight">{plan.name}</div>
        <div className="text-white/50 text-[12px] mt-0.5">{plan.tagline}</div>

        {/* Price block with strike-through when a promo is active */}
        <div className="mt-4 flex items-baseline gap-2">
          {plan.promo_active && plan.regular_price > plan.price && (
            <span className="text-white/35 font-mono text-lg tabular-nums line-through">${plan.regular_price}</span>
          )}
          <span
            className="text-white font-mono text-4xl tabular-nums"
            style={{ textShadow: `0 0 16px ${accent}66` }}
            data-testid={`plan-price-${plan.id}`}
          >
            ${plan.price}
          </span>
          <span className="text-white/40 text-[12px]">/mo</span>
          {saved > 0 && (
            <span
              className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-mono tracking-widest uppercase bg-[var(--bull)]/15 text-[var(--bull)] border border-[var(--bull)]/40"
              data-testid={`plan-save-${plan.id}`}
            >
              Save {saved}%
            </span>
          )}
        </div>

        <ul className="mt-5 space-y-2 text-[13px] text-white/80">
          {(plan.features || []).map((f, i) => (
            <li key={`f-${plan.id}-${i}`} className="flex items-start gap-2">
              <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accent }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        {plan.locked && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <div className="label-mono text-white/40 mb-1.5">Locked on Free</div>
            <ul className="space-y-1 text-[12px] text-white/45">
              {plan.locked.map((f, i) => (
                <li key={`l-${plan.id}-${i}`} className="flex items-start gap-2">
                  <XIcon className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <button
        onClick={() => !isFree && onUpgrade(plan.id)}
        disabled={disabled || busyPlan === plan.id}
        className={`relative mt-5 w-full py-2.5 rounded-full text-sm font-semibold transition-all ${
          disabled
            ? "bg-white/[0.04] text-white/40 cursor-not-allowed"
            : isFlagship
              ? "bg-[#ffd166] text-black hover:bg-[#ffd166]/90"
              : isHighlighted
                ? "bg-[var(--bull)] text-black hover:bg-[var(--bull)]/90"
                : "bg-white/[0.06] text-white border border-white/15 hover:border-white/40"
        }`}
        data-testid={`plan-cta-${plan.id}`}
      >
        {busyPlan === plan.id ? "Redirecting…" : cta}
      </button>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [promotion, setPromotion] = useState({ active: false });
  const [busyPlan, setBusyPlan] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/api/plans").then(({ data }) => {
      setPlans(data.plans || []);
      setPromotion(data.promotion || { active: false });
    }).catch(() => {});
  }, []);

  const upgrade = async (planId) => {
    if (!user) { navigate("/login", { state: { redirect: "/pricing" } }); return; }
    if (planId === "free") return;
    setBusyPlan(planId);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/api/checkout/session", { plan_id: planId, origin_url });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
      setBusyPlan(null);
    }
  };

  const currentPlan = user?.plan || "free";
  const isTrial = !!user?.trial_active;

  return (
    <div className="min-h-screen flex flex-col" data-testid="pricing-page">
      <Header />
      <PromoBanner />
      <main id="main-content" className="flex-1 max-w-[1200px] w-full mx-auto px-4 sm:px-6 py-12 sm:py-16 relative">
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "radial-gradient(800px 500px at 50% 0%, rgba(0,255,156,0.14), transparent 60%)" }} />
        <div className="relative text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md mb-5">
            <Sparkles className="w-3.5 h-3.5 text-white" />
            <span className="text-xs text-white/80 tracking-tight">Pricing</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-semibold tracking-tight leading-[1.05]" data-testid="pricing-headline">
            {HEADLINE}
          </h1>
          <p className="text-white/60 text-base sm:text-lg mt-4 max-w-2xl mx-auto leading-relaxed" data-testid="pricing-subheadline">
            {SUBHEADLINE}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12px] font-mono text-white/55" data-testid="pricing-highlights">
            {HIGHLIGHTS.map((h) => (
              <span key={h} className="inline-flex items-center gap-1.5">
                <Check className="w-3 h-3 text-[var(--bull)]" /> {h}
              </span>
            ))}
          </div>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {plans.map((p) => (
            <PlanCol key={p.id} plan={p} currentPlan={currentPlan} isTrial={isTrial}
                     onUpgrade={upgrade} busyPlan={busyPlan} promotion={promotion} />
          ))}
        </div>

        <div className="hidden lg:block mt-14" data-testid="compare-table">
          <div className="label-mono text-white/50 mb-3">Full comparison</div>
          <div className="card-surface p-2 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/55 text-[11px] font-mono tracking-widest uppercase">
                  <th className="text-left px-4 py-3">Feature</th>
                  <th className="px-4 py-3">Free</th>
                  <th className="px-4 py-3" style={{ color: "var(--bull)" }}>Premium</th>
                  <th className="px-4 py-3" style={{ color: "#ffd166" }}>Executive</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row, i) => (
                  <tr key={`cmp-${row.label}-${i}`} className="border-t border-white/5">
                    <td className="text-left px-4 py-2.5 text-white/85">{row.label}</td>
                    <td className="text-center px-4 py-2.5"><Cell value={row.free}      accent="#9aa0aa" /></td>
                    <td className="text-center px-4 py-2.5"><Cell value={row.premium}   accent="var(--bull)" /></td>
                    <td className="text-center px-4 py-2.5"><Cell value={row.executive} accent="#ffd166" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-14" data-testid="pricing-faq">
          <div className="label-mono text-white/50 mb-3">Frequently asked</div>
          <div className="card-surface p-2">
            {FAQ.map((f, i) => (
              <button
                key={`faq-${i}-${f.q}`}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full text-left px-4 py-3.5 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-white text-[14px] font-medium tracking-tight">{f.q}</span>
                  <span className="text-white/40 font-mono text-xs">{openFaq === i ? "−" : "+"}</span>
                </div>
                {openFaq === i && (
                  <p className="text-white/65 text-[13px] leading-relaxed mt-2">{f.a}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
